import { Link } from '../../../components/Link';
import {
  Lead,
  LearnCode,
  LearnList,
  LearnPageLayout,
  LearnSection,
  LearnSubsection,
  LearnTable,
} from './LearnPageLayout';
import { learnArticle } from './learnArticles';

const meta = learnArticle('disposable-email-addresses');

export default function DisposableEmailPage() {
  return (
    <LearnPageLayout
      content={{
        meta,
        definition:
          'A disposable email address is a temporary inbox created to receive one confirmation message and then abandoned. Providers spin up new domains constantly, so any blocklist is out of date the week it is published. The durable defence is detecting the pattern rather than maintaining the list.',
        faq: FAQ,
        body: <Body />,
      }}
    />
  );
}

function Body() {
  return (
    <>
      <LearnSection title="Why blocklists decay">
        <p>
          A disposable-domain blocklist is a snapshot of a set that changes daily. The economics
          are entirely one-sided: registering a domain costs a few dollars and can be automated;
          discovering it, verifying it and publishing it takes a human somewhere.
        </p>
        <LearnList>
          <li>
            <Lead>New domains arrive faster than lists update.</Lead> A provider can rotate through
            hundreds, and many are never listed anywhere at all.
          </li>
          <li>
            <Lead>Some services offer private domains</Lead> — a paid tier where the domain is
            yours alone. By construction it appears on no public list.
          </li>
          <li>
            <Lead>Lists go stale in both directions.</Lead> They accumulate dead domains that now
            belong to somebody legitimate, so an old list starts producing false positives on real
            businesses.
          </li>
        </LearnList>
        <p>
          Blocklists are still worth having — they catch the lazy majority for almost no effort. The
          mistake is treating one as a solution rather than as one cheap input among several.
        </p>
      </LearnSection>

      <LearnSection title="What survives the list going stale">
        <p>
          These are properties of how disposable services work, not of any particular domain, so
          they keep holding when the domain is new.
        </p>
        <LearnTable
          head={['Signal', 'What it indicates', 'How reliable']}
          rows={[
            ['Domain registered days ago', 'Nearly no legitimate signups use a week-old domain', <Lead key="a">Strong</Lead>],
            ['MX points at a known disposable host', 'Domain is new, infrastructure is not', <Lead key="b">Strong — this is the one that generalises</Lead>],
            ['No website at the domain, only MX', 'A mail-only domain with no business behind it', 'Moderate'],
            ['Many signups from one domain in minutes', 'Automated use, whatever the domain is', <Lead key="c">Strong in aggregate</Lead>],
            ['Random-looking local part', 'Generated rather than chosen', 'Weak alone — real addresses look odd too'],
            ['Address never engages after signup', 'Confirms it retrospectively', 'Strong, but arrives too late to gate on'],
          ]}
        />
        <p>
          The MX row is the important one and the reason pattern-detection beats list-maintenance.
          Disposable services register domains cheaply but do not rebuild mail infrastructure for
          each — a hundred fresh domains routinely point at the same handful of mail hosts.
        </p>
        <LearnCode>
          {`dig +short MX some-brand-new-domain.example
→ 10 mx.<a host that also serves 200 other throwaway domains>

The domain is unlisted. The infrastructure behind it is not.`}
        </LearnCode>
      </LearnSection>

      <LearnSection title="Not the same as a forwarding alias">
        <p>
          The distinction that causes the most damage when it is missed. Apple Hide My Email,
          Firefox Relay, DuckDuckGo Duck Address and SimpleLogin all produce an address that looks
          throwaway and behaves nothing like one.
        </p>
        <LearnTable
          head={['', 'Disposable inbox', 'Privacy forwarding alias']}
          rows={[
            ['Lifetime', 'Minutes to hours', <Lead key="a">Indefinite</Lead>],
            ['Delivers to a real person', 'No, after the first message', <Lead key="b">Yes, permanently</Lead>],
            ['Reads your later mail', 'No', <Lead key="c">Yes</Lead>],
            ['Typical user', 'Avoiding a signup gate', 'A paying customer who values privacy'],
            ['Blocking them costs you', 'Little', <Lead key="d">Real revenue</Lead>],
          ]}
        />
        <p>
          Apple's relay in particular is used by ordinary iPhone owners who tapped the default
          option. Blocking it as "disposable" refuses good customers who will not come back and
          will not tell you why. If you block only one category, block the first column.
        </p>
      </LearnSection>

      <LearnSection title="What to do at signup">
        <LearnSubsection title="Score, do not gate">
          <p>
            A hard block on a disposable domain is the most common design and the most brittle: a
            false positive is a lost customer with no path forward, and the true positives simply
            try the next domain. Treating it as one input into a risk score keeps the information
            without the cliff edge.
          </p>
        </LearnSubsection>
        <LearnSubsection title="Match the friction to the risk">
          <p>
            The useful middle ground is a step-up rather than a refusal. Require email confirmation
            before the benefit rather than before the account; withhold the free trial rather than
            the signup; ask for a second factor. Someone who wanted a throwaway inbox will not
            complete any of those, and a real customer will.
          </p>
        </LearnSubsection>
        <LearnSubsection title="Decide what you are actually protecting">
          <p>
            The reason to care is almost never the address itself — it is a repeated free trial, a
            referral bonus, or vote manipulation. Gate <em>that</em>, and the address becomes one
            signal among several rather than the whole decision. It also overlaps heavily with{' '}
            <Link to="/learn/multi-accounting" className="text-accent underline underline-offset-4">
              multi-accounting
            </Link>
            , which is usually the same person pursuing the same benefit by a different route.
          </p>
        </LearnSubsection>
        <LearnSubsection title="Expect it in your deliverability numbers">
          <p>
            Disposable addresses stop accepting mail once abandoned, so they turn into hard bounces
            weeks later and quietly inflate your{' '}
            <Link to="/learn/email-bounce-rate" className="text-accent underline underline-offset-4">
              bounce rate
            </Link>
            . A list with a long tail of them looks like a list that was never maintained.
          </p>
        </LearnSubsection>
      </LearnSection>
    </>
  );
}

const FAQ = [
  {
    q: 'What is a disposable email address?',
    a: 'A temporary inbox created to receive a single confirmation message and then abandoned, usually to get past a signup gate without giving a real address. Services offering them rotate domains constantly, which is why blocking by domain name alone stops working quickly.',
  },
  {
    q: 'Why do disposable email blocklists stop working?',
    a: 'Because they are a snapshot of a set that changes daily. Registering a domain is cheap and automatable; finding, verifying and publishing it is not. Lists also accumulate dead domains that now belong to legitimate businesses, so an old list produces false positives too.',
  },
  {
    q: 'What works better than a blocklist?',
    a: 'Detecting the pattern instead of the name. Domain age, whether the MX points at infrastructure already known to serve throwaway domains, whether there is a website at all, and signup velocity from one domain. The MX signal generalises best — new domains, same mail hosts.',
  },
  {
    q: 'Is Apple Hide My Email a disposable address?',
    a: 'No, and treating it as one is expensive. Relay aliases from Apple, Firefox and DuckDuckGo forward indefinitely to a real person who reads their mail. They are used by ordinary customers who tapped the default option, and blocking them loses real revenue silently.',
  },
  {
    q: 'Should I block disposable addresses at signup?',
    a: 'Usually not outright. A block turns a false positive into a lost customer while the true positives just try another domain. Score it, and add friction proportionate to risk — withhold the free trial rather than the account, or require confirmation before the benefit.',
  },
  {
    q: 'Do disposable addresses affect deliverability?',
    a: 'Yes, with a delay. They stop accepting mail once abandoned, so they become hard bounces weeks after signup and inflate your bounce rate. A list carrying a long tail of them looks, to a mailbox provider, exactly like a list that was never maintained.',
  },
];
